import { Input } from "@/app/components/input";
import { Select } from "@/app/components/select";
import type { Category, CategoryAttributeDefinition } from "@/app/api/auth-client";

export function attributeDefsForCategory(
  categories: Category[] | null | undefined,
  categoryId: string
): CategoryAttributeDefinition[] {
  const c = categories?.find((x) => x.category_id === categoryId);
  const raw = c?.attribute_definitions;
  return Array.isArray(raw) && raw.length > 0 ? raw : [];
}

/** Serialize API value for controlled string state. */
export function listingAttrValueToString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return String(v);
}

/** Build attributes object for create/replace PATCH from form strings. */
export function buildListingAttributesPayload(
  defs: CategoryAttributeDefinition[],
  form: Record<string, string>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const d of defs) {
    const raw = (form[d.key] ?? "").trim();
    const t = (d.type || "string").toLowerCase();
    if (raw === "" && !d.required) {
      continue;
    }
    switch (t) {
      case "number": {
        const n = parseFloat(raw.replace(",", "."));
        if (!Number.isNaN(n)) {
          out[d.key] = n;
        }
        break;
      }
      case "boolean":
        out[d.key] = raw === "true";
        break;
      default:
        out[d.key] = raw;
    }
  }
  return out;
}

export function validateListingAttributeForm(
  defs: CategoryAttributeDefinition[],
  form: Record<string, string>
): string | null {
  for (const d of defs) {
    if (!d.required) continue;
    const v = (form[d.key] ?? "").trim();
    if (v === "") {
      return `Preencha: ${d.label_pt || d.key}`;
    }
  }
  return null;
}

interface CategoryListingAttributeFieldsProps {
  defs: CategoryAttributeDefinition[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}

export function CategoryListingAttributeFields({
  defs,
  values,
  onChange,
}: CategoryListingAttributeFieldsProps) {
  if (!defs.length) return null;
  return (
    <div className="space-y-4 border-t border-border pt-4 mt-2">
      <p className="text-sm font-medium">Detalhes do anúncio (categoria)</p>
      <p className="text-xs text-muted-foreground">
        Campos definidos para a categoria selecionada. Campos marcados com * são obrigatórios.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
        {defs.map((d) => {
          const t = (d.type || "string").toLowerCase();
          const label = (d.label_pt || d.key).trim() || d.key;
          const suffix = d.required ? " *" : "";
          if (t === "boolean") {
            return (
              <label
                key={d.key}
                className="flex items-center gap-2 cursor-pointer sm:col-span-2 min-h-[2.5rem]"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border shrink-0"
                  checked={values[d.key] === "true"}
                  onChange={(e) => onChange(d.key, e.target.checked ? "true" : "false")}
                />
                <span className="text-sm">
                  {label}
                  {suffix}
                </span>
              </label>
            );
          }
          if (t === "enum" && d.options && d.options.length > 0) {
            return (
              <div key={d.key} className="min-w-0">
                <label className="block mb-2 text-sm">
                  {label}
                  {suffix}
                </label>
                <Select
                  className="w-full"
                  value={values[d.key] ?? ""}
                  onChange={(e) => onChange(d.key, e.target.value)}
                >
                  <option value="">{d.required ? "Selecione…" : "(opcional)"}</option>
                  {d.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Select>
              </div>
            );
          }
          if (t === "number") {
            return (
              <div key={d.key} className="min-w-0">
                <label className="block mb-2 text-sm">
                  {label}
                  {suffix}
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="w-full"
                  value={values[d.key] ?? ""}
                  onChange={(e) => onChange(d.key, e.target.value)}
                  placeholder="0"
                />
              </div>
            );
          }
          return (
            <div key={d.key} className="min-w-0">
              <label className="block mb-2 text-sm">
                {label}
                {suffix}
              </label>
              <Input
                className="w-full"
                value={values[d.key] ?? ""}
                onChange={(e) => onChange(d.key, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
